import {createSlice, PayloadAction} from '@reduxjs/toolkit';

import {combineEpics} from 'redux-observable';

import {catchError, mergeMap, map, repeatWhen, takeUntil, delay, filter} from 'rxjs/operators';

import {from, of, EMPTY} from 'rxjs';

import type {GatewayError} from '@yandex-data-ui/gateway';

import {WikiEpic} from '../types';

const POLLING_DELAY = 1000;

interface StartPollPayload {
    taskId: string;
}
interface StopPollPayload {
    taskId: string;
}
interface ReStartPollPayload {
    taskId: string;
}

interface SetStatusPayload {
    taskId: string;
    data: {
        status: string;
        percentage?: string;
        details?: string;
        result?: string;
    };
}

export interface AsyncOperationsState {
    [taskId: string]: AsyncOperationTaskState;
}

export interface AsyncOperationTaskState {
    status: string;
    percentage?: string;
    details?: string;
    result?: string;
}

const initialState: AsyncOperationsState = {};

const {reducer, actions} = createSlice({
    name: 'async-operations',
    initialState,
    reducers: {
        setStatus(state, {payload: {taskId, data}}: PayloadAction<SetStatusPayload>) {
            state[taskId] = data;
        },
        startPoll(_state, _payload: PayloadAction<StartPollPayload>) {},
        stopPoll(_state, _payload: PayloadAction<StopPollPayload>) {},
        restartPoll(_state, _payload: PayloadAction<ReStartPollPayload>) {},
    },
});

const startAsyncOperationEpic: WikiEpic = (action$, _state, {sdk}) =>
    action$.pipe(
        filter(actions.startPoll.match),
        mergeMap((action) => {
            return of(action).pipe(
                mergeMap(({payload: {taskId}}) => {
                    return from(
                        sdk.wiki.asyncOperations({
                            id: taskId,
                        }),
                    ).pipe(
                        map(({status, percentage, details, result}) => {
                            return actions.setStatus({
                                taskId,
                                data: {
                                    status,
                                    percentage,
                                    details,
                                    result,
                                },
                            });
                        }),
                        catchError((error: GatewayError) => {
                            return of(
                                actions.setStatus({
                                    taskId,
                                    data: {
                                        status: 'failed',
                                        details: error.message,
                                    },
                                }),
                            );
                        }),
                    );
                }),
                repeatWhen((complete$) => complete$.pipe(delay(POLLING_DELAY))),
                takeUntil(
                    action$.pipe(filter(actions.stopPoll.match)).pipe(
                        filter(({payload: {taskId}}) => {
                            return action.payload.taskId === taskId;
                        }),
                    ),
                ),
            );
        }),
    );

const onAsyncOperationStatusSetEpic: WikiEpic = (action$) =>
    action$.pipe(
        filter(actions.setStatus.match),
        mergeMap(
            ({
                payload: {
                    taskId,
                    data: {status},
                },
            }) => {
                if (status === 'success' || status === 'failed') {
                    return of(actions.stopPoll({taskId}));
                }

                return EMPTY;
            },
        ),
    );

const onAsyncOperationRestartEpic: WikiEpic = (action$) =>
    action$.pipe(
        filter(actions.restartPoll.match),
        mergeMap(({payload: {taskId}}) => {
            return of(actions.stopPoll({taskId}), actions.startPoll({taskId}));
        }),
    );

const epic = combineEpics<WikiEpic>(
    startAsyncOperationEpic,
    onAsyncOperationStatusSetEpic,
    onAsyncOperationRestartEpic,
);

export {epic, reducer, actions};
